import { Head } from "@inertiajs/react"

import { HERO_WIDTHS, buildImageUrl, buildSrcSet } from "../../utils/imageUrl"

/**
 * Preloads the item-page hero so the LCP element is discoverable by the preload
 * scanner rather than waiting on hydration.
 *
 * `widths` MUST be the same list the hero <img> advertises, and `sizes` must be
 * the same string — the browser runs candidate selection twice, once for this
 * link and once for the <img>, and any difference makes it download two
 * variants of the LCP image instead of one. Defaults to HERO_WIDTHS so the
 * matching case is the effortless one.
 */
export const HeroPreload = ({ src, sizes, widths = HERO_WIDTHS }) => {
    if (!src) return null

    const href = buildImageUrl(src, {
        width: widths[widths.length - 1],
        quality: 70,
    })

    // An unservable src builds to "" — a preload with href="" fetches the
    // document itself, so emit nothing (the hero shows its placeholder).
    if (!href) return null

    const srcSet = buildSrcSet(src, widths, 70)

    // A src the CDN does not rewrite has no srcset. Inertia's SSR head prints
    // an undefined attribute as the string "undefined", which the browser
    // would then preload as a URL, so the pair is left off entirely.
    const candidates = srcSet ? { imageSrcSet: srcSet, imageSizes: sizes } : {}

    return (
        <Head>
            <link
                rel="preload"
                as="image"
                href={href}
                {...candidates}
                fetchPriority="high"
            />
        </Head>
    )
}
